/* =======================================================================
   MISRDELIGHTS — misr-motion.js
   Project 5 (Website Lead) build · scroll-reveal motion system (item 1 of 4).

   WHAT IT DOES
   On the HOMEPAGE only, arms each top-level Shopify section (except the first,
   the hero) with a "barely-there" fade-up that triggers as it scrolls into view.
   Reveal-once: a section animates a single time, then is left alone.

   WHY IT IS SAFE
   - Adds html.misr-motion-ready ONLY after confirming IntersectionObserver and
     (separately) that motion is allowed. The CSS hides content only under that
     class, so if this script never runs / errors, NOTHING is ever hidden.
   - prefers-reduced-motion: we do not even arm the observer. Sections are left
     fully visible and untouched. (CSS also enforces this as a second line.)
   - Homepage-scoped: bails immediately on any other template.
   - No dependencies, no theme.js coupling, defer-loaded.

   PRESTIGE NOTES (from "Working with Prestige")
   - Sections render as <div/section id="shopify-section-XX">. We arm those
     wrappers directly, so no per-section markup edit is needed.
   - The theme editor reloads sections on edit; we listen for shopify:section:load
     so a section dropped in the editor still gets armed (and is revealed
     immediately, since editor users are not "scrolling" to it).
   ========================================================================== */
(function () {
  'use strict';

  /* DISABLED 2026-06-07 (Project 5, Tariq) — homepage section scroll-reveal
     switched off at the entry point. Returning before anything is armed means
     html.misr-motion-ready is never set, so: (a) no section fade-up; (b) the
     about-story image-settle, which is gated on .misr-motion-ready, falls back
     to its natural scale(1) — never stuck zoomed; (c) the trust-ribbon weight
     rules in misr-motion.css are NOT gated on this class and keep working. The
     full reveal logic is retained below, unreached, so re-enabling is a one-line
     revert (remove this return). */
  return;

  /* 1 — Homepage only. Prestige sets template info on <body>; we check the
     index template via the body class Shopify emits, with a URL fallback. */
  function isHomepage() {
    var b = document.body;
    if (b && b.classList) {
      // Shopify online store adds template-specific classes; index = home.
      if (b.classList.contains('template-index')) return true;
      if (b.getAttribute('data-template') === 'index') return true;
    }
    // Fallback: root path or /?... with no extra segments.
    var p = window.location.pathname.replace(/\/+$/, '');
    return p === '' || p === '/';
  }

  if (!isHomepage()) return;

  /* 2 — Capability + preference gates. If either fails, do nothing at all:
     leave the page exactly as the server rendered it (fully visible). */
  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { reduceMotion = false; }

  if (reduceMotion) return;
  if (!('IntersectionObserver' in window)) return;

  var SELECTOR = 'main#main > .shopify-section, main#main > div[id^="shopify-section-"]';
  var STAGGER_MS = 90;   // gap between sibling reveals so they do not move in lockstep
  var THRESHOLD = 0.12;  // ~12% of the section in view triggers it

  function armCollection() {
    var sections = document.querySelectorAll(SELECTOR);
    if (!sections.length) return;

    // Signal the CSS it is safe to hide-then-reveal. Done here (not at parse
    // time) so content is only ever hidden once we KNOW we can reveal it.
    document.documentElement.classList.add('misr-motion-ready');

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        obs.unobserve(el); // reveal-once

        // Stagger by the element's index among armed siblings.
        var delay = parseInt(el.getAttribute('data-misr-index'), 10) || 0;
        window.setTimeout(function () {
          el.classList.add('is-revealed');
        }, delay * STAGGER_MS);

        // Drop will-change after the transition settles (0.7s + buffer).
        window.setTimeout(function () {
          el.classList.add('is-settled');
        }, delay * STAGGER_MS + 900);
      });
    }, { threshold: THRESHOLD, rootMargin: '0px 0px -8% 0px' });

    sections.forEach(function (section, i) {
      // Skip the first section (the hero). Above-the-fold content fading in
      // on load reads as slow, not premium — the reveal is for scrolled-to
      // sections only.
      if (i === 0) return;

      section.classList.add('misr-reveal');
      section.setAttribute('data-misr-index', i - 1);
      observer.observe(section);
    });

    return observer;
  }

  function init() { armCollection(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* 3 — Theme editor support. When a merchant adds/reorders a section in the
     customizer, Shopify reloads it. Reveal it immediately (no scroll happens
     in the editor) so it never sits invisible while editing. */
  document.addEventListener('shopify:section:load', function (evt) {
    if (reduceMotion) return;
    var sec = evt.target;
    if (!sec || !sec.classList) return;
    document.documentElement.classList.add('misr-motion-ready');
    sec.classList.add('misr-reveal', 'is-revealed', 'is-settled');
  });
})();
